import axios, { AxiosError } from "axios";
import { attachRateLimitRetry } from "./withRateLimitRetry";

export interface HealthMetrics {
  status: "ok" | "error";
  info: Record<string, { status: "up" | "down"; message?: string }>;
  users: {
    /** Distinct users holding a live socket right now. */
    live: number;
    /** Total sockets — exceeds `live` when someone has several tabs open. */
    liveConnections: number;
    /** Users the API has seen within `windowMinutes`. */
    recentlyActive: number;
    windowMinutes: number;
  };
  instance: {
    pid: number;
    uptimeSeconds: number;
    requests: {
      inFlight: number;
      lastMinute: number;
      totalSinceBoot: number;
    };
  };
}

const API_BASE_URL = import.meta.env.VITE_BACKEND_URL;

// Bare baseURL: /health/metrics sits outside the /api prefix, so none of the
// other clients in this folder can reach it.
const healthApiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Registered before any reshaping, which discards error.response — the retry
// needs its status and Retry-After.
attachRateLimitRetry(healthApiClient);

/**
 * Live usage and traffic. Super admin only — callers must gate on role, since
 * this is a 403 for everyone else.
 */
export async function getHealthMetrics(): Promise<HealthMetrics> {
  try {
    const response = await healthApiClient.get<HealthMetrics>("/health/metrics");
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw {
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to load metrics",
        statusCode: error.response?.status,
      };
    }
    throw { message: "Failed to load metrics", statusCode: 500 };
  }
}
